import { OfferStatusCode } from "../../models/models";
import { ServiceCompletionDetails, ProgressUpdate, UpdateType } from "../../../servicing/models/executionModels";
import { ServiceReview } from "../../../servicing/models/reviewModels";
import {
  MilestoneType,
  MilestoneStatus,
  LogbookMilestoneEntry,
  LogbookProgressNote,
  LogbookEntry,
} from "../../../servicing/components/rfsDetail/logbookUtils";

// Re-export types so consumers can import from here
export type { MilestoneType, MilestoneStatus, LogbookMilestoneEntry, LogbookProgressNote, LogbookEntry };
export { milestoneColorMap } from "../../../servicing/components/rfsDetail/logbookUtils";

// ===================================================================
//  Offer-specific milestone types (subset of the full RFS set)
//  The offer logbook starts from serviceConfirmed — no "published" or "offerAccepted"
// ===================================================================
export type OfferMilestoneType =
  | "serviceConfirmed"
  | "workStarted"
  | "workCompleted"
  | "ownerConfirmed"
  | "reviewLeft";

// ===================================================================
//  Mapping: MilestoneType -> backend ProgressUpdateType
// ===================================================================
const milestoneToUpdateType: Partial<Record<OfferMilestoneType, UpdateType>> = {
  serviceConfirmed: UpdateType.ProviderConfirmed,
  workStarted: UpdateType.WorkStarted,
  workCompleted: UpdateType.WorkCompleted,
  ownerConfirmed: UpdateType.OwnerConfirmed,
  reviewLeft: UpdateType.ReviewReceived,
};

// ===================================================================
//  Ordinal lookup
// ===================================================================
const OFFER_MILESTONE_ORDINALS: Record<OfferMilestoneType, number> = {
  serviceConfirmed: 0,
  workStarted: 1,
  workCompleted: 2,
  ownerConfirmed: 3,
  reviewLeft: 4,
};

// ===================================================================
//  Status code -> current milestone mapping
// ===================================================================
function getCurrentOfferMilestoneType(statusCode: OfferStatusCode): OfferMilestoneType {
  switch (statusCode) {
    case OfferStatusCode.confirmed:
      return "serviceConfirmed";
    case OfferStatusCode.workInProgress:
      return "workStarted";
    case OfferStatusCode.workCompleted:
      return "workCompleted";
    case OfferStatusCode.serviceCompleted:
    case OfferStatusCode.archived:
      return "reviewLeft";
    default:
      return "serviceConfirmed";
  }
}

// ===================================================================
//  Builder params
// ===================================================================
export interface BuildOfferLogbookParams {
  statusCode: OfferStatusCode;
  confirmedByProviderDate?: string;
  confirmedArrivalDate?: string;
  confirmedArrivalTimeFrom?: string;
  confirmedArrivalTimeTo?: string;
  confirmationMessage?: string;
  workStartedDate?: string;
  completion?: ServiceCompletionDetails | null;
  progressUpdates: ProgressUpdate[];
  reviews: ServiceReview[];
  hasReviewed: boolean;
}

// ===================================================================
//  Builder function
// ===================================================================
export function buildOfferLogbookEntries(params: BuildOfferLogbookParams): LogbookEntry[] {
  const {
    statusCode,
    confirmedByProviderDate,
    confirmedArrivalDate,
    confirmedArrivalTimeFrom,
    confirmedArrivalTimeTo,
    confirmationMessage,
    workStartedDate,
    completion,
    progressUpdates,
    reviews,
    hasReviewed,
  } = params;

  const currentType = getCurrentOfferMilestoneType(statusCode);
  const currentOrdinal = OFFER_MILESTONE_ORDINALS[currentType];

  function milestoneStatus(type: OfferMilestoneType): MilestoneStatus {
    const ord = OFFER_MILESTONE_ORDINALS[type];
    if (ord < currentOrdinal) return "completed";
    if (ord === currentOrdinal) return "current";
    return "pending";
  }

  // Build a map of backend milestone timestamps
  const backendTimestamps: Partial<Record<OfferMilestoneType, string>> = {};
  for (const [mType, uType] of Object.entries(milestoneToUpdateType) as [OfferMilestoneType, UpdateType][]) {
    const backendRow = progressUpdates.find((u) => u.updateType === uType);
    if (backendRow) {
      backendTimestamps[mType] = backendRow.createdDate;
    }
  }

  function pickTimestamp(type: OfferMilestoneType, fallback?: string): string | undefined {
    return backendTimestamps[type] || fallback;
  }

  const entries: LogbookEntry[] = [];

  // 1. Service Confirmed
  const confirmedStatus = milestoneStatus("serviceConfirmed");
  entries.push({
    id: "ms-serviceConfirmed",
    kind: "milestone",
    type: "serviceConfirmed",
    ordinal: 0,
    timestamp: confirmedStatus !== "pending"
      ? pickTimestamp("serviceConfirmed", confirmedByProviderDate)
      : undefined,
    status: confirmedStatus,
    data: confirmedStatus !== "pending"
      ? {
          confirmedArrivalDate,
          confirmedArrivalTimeFrom,
          confirmedArrivalTimeTo,
          confirmationMessage,
        }
      : {},
  });

  // 2. Work Started
  const workStartedStatus = milestoneStatus("workStarted");
  entries.push({
    id: "ms-workStarted",
    kind: "milestone",
    type: "workStarted",
    ordinal: 1,
    timestamp: workStartedStatus !== "pending"
      ? pickTimestamp("workStarted", workStartedDate)
      : undefined,
    status: workStartedStatus,
    data: {},
  });

  // 2.5: Progress notes — interleaved between workStarted and workCompleted
  const progressNotes = progressUpdates.filter(
    (u) => u.updateType === UpdateType.ProgressNote
  );
  for (const note of progressNotes) {
    entries.push({
      id: `pn-${note.id}`,
      kind: "progressNote",
      timestamp: note.createdDate,
      authorName: note.authorName,
      message: note.message,
    });
  }

  // 3. Work Completed
  const workCompletedStatus = milestoneStatus("workCompleted");
  entries.push({
    id: "ms-workCompleted",
    kind: "milestone",
    type: "workCompleted",
    ordinal: 2,
    timestamp: workCompletedStatus !== "pending"
      ? pickTimestamp("workCompleted", completion?.completedByProviderDate)
      : undefined,
    status: workCompletedStatus,
    data: workCompletedStatus !== "pending"
      ? { completion }
      : {},
  });

  // 4. Owner Confirmed
  const ownerConfirmedStatus = milestoneStatus("ownerConfirmed");
  entries.push({
    id: "ms-ownerConfirmed",
    kind: "milestone",
    type: "ownerConfirmed",
    ordinal: 3,
    timestamp: ownerConfirmedStatus !== "pending" && completion?.confirmedByOwnerDate
      ? pickTimestamp("ownerConfirmed", completion.confirmedByOwnerDate)
      : undefined,
    status: ownerConfirmedStatus,
    data: {},
  });

  // 5. Review
  const reviewStatus = milestoneStatus("reviewLeft");
  entries.push({
    id: "ms-reviewLeft",
    kind: "milestone",
    type: "reviewLeft",
    ordinal: 4,
    timestamp: reviewStatus !== "pending"
      ? pickTimestamp("reviewLeft", reviews.length > 0 ? reviews[0].createdDate : undefined)
      : undefined,
    status: reviewStatus,
    data: reviewStatus !== "pending" ? { reviews, hasReviewed } : {},
  });

  return entries;
}
